import { Injectable, inject } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, shareReplay, tap } from 'rxjs/operators';
import { AgentApiService } from '../../../../shared/services/agent-api/agent-api.service';
import { TokenService } from '../../../../shared/services/token/token.service';
import { LoggerService } from '../../../../services/logger-services/logger.service';

export interface PresignedUrlApiResponse {
  status: string;
  code: number;
  message: string | null;
  data: {
    file_path: string;
    presigned_url: string;
    expires_in: number;
  };
}

interface CachedPresignedUrl {
  presignedUrl: string;
  expiresAt: number;
}

export const RIA_AGENT_ID = 'ria_agent';
const PRESIGNED_URL_ENDPOINT_KEY = 'get_document_presigned_url';
const GRI_API_BASE_URL_KEY = 'griApiBaseUrl';
const CACHE_BUFFER_MS = 60_000;

@Injectable({
  providedIn: 'root',
})
export class AgentDocumentPresignedUrlService {
  private readonly agentApiService = inject(AgentApiService);
  private readonly tokenService = inject(TokenService);
  private readonly logger = inject(LoggerService);

  private readonly cache = new Map<string, CachedPresignedUrl>();
  private readonly inflight = new Map<string, Observable<string>>();

  getPresignedUrl(filePath: string, agentId: string = RIA_AGENT_ID): Observable<string> {
    const normalizedPath = filePath.trim();
    const cacheKey = `${agentId}::${normalizedPath}`;
    const cached = this.getValidCached(cacheKey);
    if (cached) {
      return of(cached);
    }

    const existing = this.inflight.get(cacheKey);
    if (existing) {
      return existing;
    }

    const endpointPath = this.agentApiService.getEndpointPath(PRESIGNED_URL_ENDPOINT_KEY, agentId);
    const baseUrl = this.agentApiService.getAuxiliaryApiBaseUrl(agentId, GRI_API_BASE_URL_KEY);

    if (!endpointPath || !baseUrl) {
      return throwError(() => new Error(
        `Presigned URL API not configured for agent: ${agentId}`
      ));
    }

    const request$ = this.agentApiService
      .post<PresignedUrlApiResponse>(
        endpointPath,
        { file_path: normalizedPath },
        this.tokenService.getAccessToken(),
        baseUrl
      )
      .pipe(
        map((response) => {
          const url = response?.data?.presigned_url;
          const expiresIn = response?.data?.expires_in ?? 3600;
          if (!url || response.code !== 200) {
            throw new Error(response?.message ?? 'Failed to get presigned URL');
          }
          this.cache.set(cacheKey, {
            presignedUrl: url,
            expiresAt: Date.now() + expiresIn * 1000 - CACHE_BUFFER_MS,
          });
          return url;
        }),
        tap({
          finalize: () => {
            this.inflight.delete(cacheKey);
          },
        }),
        catchError((error) => {
          this.inflight.delete(cacheKey);
          this.logger.logError(
            (error as Error)?.message ?? 'Presigned URL request failed',
            'AgentDocumentPresignedUrlService.getPresignedUrl'
          );
          return throwError(() => error);
        }),
        shareReplay(1)
      );

    this.inflight.set(cacheKey, request$);
    return request$;
  }

  invalidate(filePath: string, agentId: string = RIA_AGENT_ID): void {
    const cacheKey = `${agentId}::${filePath.trim()}`;
    this.cache.delete(cacheKey);
    this.inflight.delete(cacheKey);
  }

  private getValidCached(cacheKey: string): string | null {
    const entry = this.cache.get(cacheKey);
    if (!entry) {
      return null;
    }
    if (Date.now() >= entry.expiresAt) {
      this.cache.delete(cacheKey);
      return null;
    }
    return entry.presignedUrl;
  }
}
